import { useCallback, useState } from 'react'
import { getBatchStatus, registerBatch, deregisterBatch } from './api'
import type {
  BatchStatusData,
  BatchRegisterParams,
  BatchDeregisterParams,
} from './types'

export function useBatchStatus() {
  const [status, setStatus] = useState<BatchStatusData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await getBatchStatus()
      setStatus(response.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setIsLoading(false)
    }
  }, [])

  return { status, isLoading, error, refetch }
}

export function useBatchRegister(onSuccess?: () => void) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const register = useCallback(
    async (params: BatchRegisterParams) => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await registerBatch(params)
        onSuccess?.()
        return response
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        setError(message)
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [onSuccess],
  )

  return { register, isLoading, error }
}

export function useBatchDeregister(onSuccess?: () => void) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deregister = useCallback(
    async (params: BatchDeregisterParams) => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await deregisterBatch(params)
        onSuccess?.()
        return response
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        setError(message)
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [onSuccess],
  )

  return { deregister, isLoading, error }
}
